import { Injectable } from '@angular/core';
import { FileDownloadService } from 'new-sysman-ui';

type TipoArchivoSoportado = 'excel' | 'csv' | 'pdf';

interface ConfiguracionTipo {
  mime: string;
  ext: string;
}

/**
 * Servicio de descarga robusto y compatible con todos los navegadores.
 *
 * Reemplaza (vía inyección de dependencias) al `FileDownloadService` de la
 * librería `new-sysman-ui`, cuya implementación revocaba el ObjectURL y
 * eliminaba el enlace de descarga de forma síncrona inmediatamente después
 * del `click()`. Como la descarga del navegador es asíncrona, esto provocaba
 * que Firefox, algunos Chromium y Safari cancelaran la descarga antes de
 * iniciarla.
 *
 * Estrategia aplicada para que la descarga funcione al 100%:
 *  - Decodificación de base64 por bloques (evita fallos con archivos grandes).
 *  - Limpieza diferida del ObjectURL y del enlace (no se revoca de inmediato).
 *  - Soporte de `msSaveOrOpenBlob` para navegadores legacy (IE/Edge clásico).
 *  - Respaldo con apertura en pestaña nueva si el atributo `download` no existe.
 */
@Injectable({ providedIn: 'root' })
export class RobustFileDownloadService extends FileDownloadService {
  /** Tiempo de espera antes de liberar el ObjectURL (alineado con FileSaver.js). */
  private static readonly REVOKE_DELAY_MS = 40_000;

  private readonly configuracionPorTipo: Record<TipoArchivoSoportado, ConfiguracionTipo> = {
    excel: {
      mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ext: 'xlsx',
    },
    csv: { mime: 'text/csv;charset=utf-8', ext: 'csv' },
    pdf: { mime: 'application/pdf', ext: 'pdf' },
  };

  override async descargarArchivo(
    base64: string,
    nombreBaseArchivo: string,
    tipo: TipoArchivoSoportado,
    mimePersonalizado?: string,
  ): Promise<void> {
    const config = this.configuracionPorTipo[tipo];

    if (!mimePersonalizado && !config) {
      console.error(`Tipo de archivo no soportado: ${tipo}. Use 'excel', 'csv' o 'pdf'.`);
      return;
    }

    const mimeFinal = mimePersonalizado ?? config.mime;
    const nombreArchivoFinal = mimePersonalizado ? nombreBaseArchivo : `${nombreBaseArchivo}.${config.ext}`;

    let blob: Blob;
    try {
      blob = this.base64ABlob(base64, mimeFinal);
    } catch (e) {
      console.error('Error al decodificar la cadena base64:', e);
      return;
    }

    this.guardarBlob(blob, nombreArchivoFinal);
  }

  /**
   * Convierte una cadena base64 a Blob procesándola por bloques.
   * Decodificar carácter por carácter en un solo arreglo puede agotar memoria
   * o lanzar RangeError con archivos grandes; el procesamiento por chunks lo evita.
   */
  private base64ABlob(base64: string, mime: string): Blob {
    const limpio = this.limpiarBase64(base64);
    const byteCharacters = atob(limpio);
    const sliceSize = 1024;
    const byteArrays: BlobPart[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array<number>(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: mime });
  }

  /** Elimina el prefijo Data URI (`data:...;base64,`) y espacios si vinieran incluidos. */
  private limpiarBase64(base64: string): string {
    const sinEspacios = (base64 ?? '').trim();
    const indiceComa = sinEspacios.indexOf(',');
    if (sinEspacios.startsWith('data:') && indiceComa !== -1) {
      return sinEspacios.substring(indiceComa + 1);
    }
    return sinEspacios;
  }

  private guardarBlob(blob: Blob, nombreArchivo: string): void {
    const nav = window.navigator as Navigator & {
      msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean;
    };

    if (typeof nav.msSaveOrOpenBlob === 'function') {
      nav.msSaveOrOpenBlob(blob, nombreArchivo);
      return;
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const soportaDownload = 'download' in link;

    link.href = url;
    link.download = nombreArchivo;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);

    if (soportaDownload) {
      this.dispararClick(link);
    } else {
      window.open(url, '_blank');
    }

    setTimeout(() => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    }, RobustFileDownloadService.REVOKE_DELAY_MS);
  }

  /** Dispara el click de forma compatible (algunos navegadores requieren MouseEvent explícito). */
  private dispararClick(elemento: HTMLAnchorElement): void {
    try {
      elemento.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    } catch {
      elemento.click();
    }
  }
}
